// 스페셜 몬스터(황금 고블린·챔피언) 순수 규칙(정본: docs/STAGE-WORLD-PRD.md 4절). DOM·three·게임 상태 import 금지 — 서버 권위로 옮겨도 같은 공식이다.
// 공용 상수(확률·보상 배율)는 content/special.ts, 여기는 그 위의 굴림·접두어·보상 계산·이벤트 번호다.
// 결정적: 같은 씨앗·같은 입력이면 같은 결과(굴림은 항상 난수를 같은 수만큼 쓴다 — 종류가 달라도 뒤 값이 흔들리지 않는다).
import { SPECIAL, SPECIAL_CHANCE, type DifficultyKey, type SpecialKind, type StageKind } from './content/special';
import { nextRand, seedRng } from './gear';

/** 몹 슬롯 표지(MobsView.special): 0 일반 */
export const MobSpecial = { None: 0, GoldGoblin: 1, Champion: 2 } as const;
export type MobSpecialId = (typeof MobSpecial)[keyof typeof MobSpecial];

/**
 * 스페셜 몬스터 이벤트 번호. 공용 Ev(events.ts)와 겹치지 않게 90번대를 쓴다(다른 레인이 Ev 끝에 번호를 늘려도 부딪히지 않게 별도 표).
 * Appear: a 종류(MobSpecial), b 몹 슬롯, c 접두어 번호(챔피언, 0..2), d 머무는 시간(초, 황금 고블린 0이면 없음). x,z 등장 자리
 * Flee: 황금 고블린이 처음 맞고 도망치기 시작. a 슬롯, b 도망 시간(초)
 * Vanish: 황금 고블린이 땅속으로 사라짐(보상 없음). x,z 자리, a 슬롯
 * Reward: 처치 보상. x,z 자리, a 골드(기본 보상, 장비 골드 배율 전), b 강화석 수, c 종류(MobSpecial), d 확정 장비 등급 단계(0 없음)
 */
export const SpecialEv = { Appear: 90, Flee: 91, Vanish: 92, Reward: 93 } as const;

/** 런타임 규칙(가안, 시뮬로 확정): 공용 상수 SPECIAL에 없는 값 */
export const SPECIAL_RT = {
  /** 황금 고블린이 맞지 않아도 머무는 최대 시간(초). 맞으면 SPECIAL.goldGoblin.fleeSec로 바뀐다 */
  appearSec: 20,
  /** 방에 들어온 뒤 황금 고블린이 나타날 때까지(초, 세계 시간) */
  spawnDelaySec: [4, 10] as const,
  /** 황금 고블린이 영웅에게서 이만큼 떨어진 자리에 나온다(m) */
  spawnDist: [6, 10] as const,
  /** 황금 고블린 렌더 크기 배율(멀리서도 '스페셜'로 읽히게, 판정 반경은 그대로) */
  goldScale: 1.35,
  /** 도망 길 앞을 내다보는 거리(m) */
  fleeLook: 1.4,
  /** 챔피언 알림 띠를 보이는 시간(초). 이름표는 살아 있는 동안 계속 */
  championBannerSec: 5,
} as const;

/** 챔피언 접두어. 효과는 작지만 실제로 적용된다(보이는데 안 걸리는 효과 금지 — game/mobs.ts·game/special.ts가 읽는다) */
export interface ChampionAffix {
  readonly id: 'swift' | 'steel' | 'flame';
  /** 이름표 접두어 */
  readonly name: string;
  /** 이동 속도 배율 */
  readonly speedMult: number;
  /** 영웅에게 주는 피해 배율 */
  readonly damageMult: number;
  /** 불꽃 오라: 반경 m 안 영웅에게 intervalSec마다 최대 HP × ratio 화상(0이면 없음) */
  readonly burn: { readonly radius: number; readonly ratio: number; readonly intervalSec: number } | null;
  /** 오라 색(0xRRGGBB, 렌더) */
  readonly color: number;
  /** 한 줄 설명(알림 띠 보조 문구) */
  readonly tip: string;
}
export const CHAMPION_AFFIXES: readonly ChampionAffix[] = [
  { id: 'swift', name: '신속', speedMult: 1.25, damageMult: 1, burn: null, color: 0xfff0a8, tip: '빠르다' },
  { id: 'steel', name: '강철', speedMult: 1, damageMult: 1.2, burn: null, color: 0xdfe6f2, tip: '타격이 묵직하다' },
  { id: 'flame', name: '불꽃 오라', speedMult: 1, damageMult: 1, burn: { radius: 2.4, ratio: 0.015, intervalSec: 1 }, color: 0xff9a3c, tip: '가까이 서면 화상' },
];

/** 한 판 스페셜 계획(판 시작에 한 번 굴린다) */
export interface SpecialPlan {
  /** 이번 판에 나오는 종류(없으면 null) */
  readonly kind: SpecialKind | null;
  /** 황금 고블린이 나오는 전투방 순번(전투방끼리 0..battleRooms-1) */
  readonly room: number;
  /** 방에 들어온 뒤 나올 때까지 세계 틱 */
  readonly delayTicks: number;
  /** 챔피언 승격 위치(0..1, 마지막 웨이브 등장 순서의 비율) */
  readonly pick: number;
  /** 챔피언 접두어 번호(CHAMPION_AFFIXES 인덱스) */
  readonly affix: number;
  /** 보상·위치 굴림용 씨앗(판 안 뒷일에 쓰는 별도 수열) */
  readonly aux: number;
}

const NONE: SpecialPlan = { kind: null, room: 0, delayTicks: 0, pick: 0, affix: 0, aux: 1 };

/**
 * 스페셜 계획 굴림. 일반 스테이지(normal)에서만 나올 수 있다 — 중간·지역 보스판은 어떤 씨앗에서도 null.
 * 한 판 최대 1마리: 한 번 굴려 [0, 황금) → 황금 고블린, [황금, 황금+챔피언) → 챔피언, 나머지 → 없음.
 * battleRooms: 이 판의 전투방 수(황금 고블린이 나올 방을 고른다).
 */
export function rollSpecialPlan(seed: number, kind: StageKind, diff: DifficultyKey, battleRooms: number, tickHz = 60): SpecialPlan {
  if (kind !== 'normal' || battleRooms <= 0) return NONE;
  const rng = seedRng(seed);
  const u = nextRand(rng), r = nextRand(rng), d = nextRand(rng), p = nextRand(rng), a = nextRand(rng), aux = (Math.floor(nextRand(rng) * 0xffffffff) >>> 0) || 1;
  const c = SPECIAL_CHANCE[diff];
  const k: SpecialKind | null = u < c.goldGoblin ? 'goldGoblin' : u < c.goldGoblin + c.champion ? 'champion' : null;
  const lo = SPECIAL_RT.spawnDelaySec[0], hi = SPECIAL_RT.spawnDelaySec[1];
  return {
    kind: k,
    room: Math.min(battleRooms - 1, Math.floor(r * battleRooms)),
    delayTicks: Math.round((lo + (hi - lo) * d) * tickHz),
    pick: p,
    affix: Math.min(CHAMPION_AFFIXES.length - 1, Math.floor(a * CHAMPION_AFFIXES.length)),
    aux,
  };
}

/** 지정 종류를 강제한 계획(DEV·QA 전용, 확률 우회). 나머지 값은 정상 굴림과 같은 씨앗에서 */
export function forcedSpecialPlan(seed: number, kind: SpecialKind, diff: DifficultyKey, battleRooms: number, tickHz = 60): SpecialPlan {
  const base = rollSpecialPlan(seed, 'normal', diff, battleRooms, tickHz);
  return { ...base, kind };
}

/** 황금 고블린 처치 골드(기본 보상 단위, 장비 골드 배율 전) = 지역 클리어 base × SPECIAL.goldGoblin.gold */
export function goldGoblinGold(regionBase: number): number {
  return Math.round(regionBase * SPECIAL.goldGoblin.gold);
}
/** 챔피언 처치 골드 = 지역 클리어 base × SPECIAL.champion.gold */
export function championGold(regionBase: number): number {
  return Math.round(regionBase * SPECIAL.champion.gold);
}
/** 황금 고블린 강화석 수(1..3, 균등). rand는 0..1 난수 */
export function goldGoblinStones(rand: () => number): number {
  const lo = SPECIAL.goldGoblin.stones[0], hi = SPECIAL.goldGoblin.stones[1];
  return lo + Math.min(hi - lo, Math.floor(rand() * (hi - lo + 1)));
}

/** 챔피언 이름표: 「접두어 몹이름」 */
export function championName(affix: number, mobName: string): string {
  const a = CHAMPION_AFFIXES[Math.max(0, Math.min(CHAMPION_AFFIXES.length - 1, affix))];
  return a.name + ' ' + mobName;
}

/** 기대 보너스 골드(기본 보상 단위 base 배): 한 판 평균 = Σ 종류 확률 × 보상 배율. 시뮬·입장 카드가 쓴다 */
export function expectedSpecialGoldMult(diff: DifficultyKey): number {
  const c = SPECIAL_CHANCE[diff];
  return c.goldGoblin * SPECIAL.goldGoblin.gold + c.champion * SPECIAL.champion.gold;
}
/** 기대 강화석(개): 황금 고블린 확률 × 평균 개수 */
export function expectedSpecialStones(diff: DifficultyKey): number {
  const s = SPECIAL.goldGoblin.stones;
  return SPECIAL_CHANCE[diff].goldGoblin * (s[0] + s[1]) / 2;
}
